// FUNCTIONALITY-101/102 / CROSSSELL-109/110 / 115C-R2-R1 — Store V2 shared UI atoms.
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  GlobalAppCloseButton,
  type GlobalAppCloseAnalyticsPayload,
} from "@asiastyle/surface-ui";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { copyText } from "./contact-manager-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { resolveProductAvailability } from "./product-availability-v2.js";
import {
  isOwnerCanaryPriceViewer,
  resolveCustomerVisiblePrice,
} from "./resolve-customer-visible-price-v2.js";
import { formatUah } from "./money-v2.js";
import { customerCodeLabel, resolveIdentityRows } from "./product-identity-v2.js";
import { emitEvent } from "./events-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import { CustomerFeedbackHost, CustomerFeedbackLauncher } from "./CustomerFeedbackSheet.js";
import {
  cardImageUrl,
  miniImageUrl,
  pdpImageUrl,
  gridImageLoadingHints,
} from "./product-media-v2.js";
import { armExplicitStoreClose } from "./post-order-session-v2.js";

const CLOSE_BUILD_ID =
  (import.meta.env.VITE_BUILD_ID as string | undefined)?.trim() || "store-v2";

export interface ChromeProps {
  title: string;
  cartCount: number;
  canGoBack: boolean;
  lang: StoreV2Lang;
  theme?: "system" | "light" | "dark";
  routeClass?: string;
  /** Global photo-search entry (lazy slot). Hidden until consent / after auth. */
  visualSearchSlot?: ReactNode;
  onBack: () => void;
  onHome: () => void;
  onCart: () => void;
  onProfile: () => void;
  /** STAGING-LAUNCH-READINESS-002-R2-R4 — top-level customer cabinet entry. */
  onCabinet?: () => void;
  onLangChange?: (lang: StoreV2Lang) => void;
  onThemeCycle?: () => void;
  onBrowserCloseFallback?: () => void;
  onRequestExitSheet?: () => void;
}

function emitCloseAnalytics(payload: GlobalAppCloseAnalyticsPayload): void {
  try {
    emitEvent("surface_close_clicked", {
      surface: payload.surface,
      route_class: payload.route_class,
      telegram_context: payload.telegram_context,
      dirty: payload.dirty,
      result: payload.result,
      build_id: payload.build_id,
    });
  } catch {
    /* never block */
  }
}

/**
 * CUSTOMER-MINIAPP-131 — single CloseControl host.
 * Portaled above page/modals (z-index) so payment/contact overlays cannot swallow the ×.
 * Header keeps a spacer so layout does not jump.
 */
export function StoreV2GlobalClose({
  lang,
  routeClass = "store",
  onBrowserCloseFallback,
  onRequestExitSheet,
}: {
  lang: StoreV2Lang;
  routeClass?: string;
  onBrowserCloseFallback?: () => void;
  /** When set, × opens the exit-retention sheet; sheet confirm calls closeMiniApp via finishCloseStore. */
  onRequestExitSheet?: () => void;
}) {
  const [mounted, setMounted] = useState(false);
  const openingRef = useRef(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const button = (
    <GlobalAppCloseButton
      surface="customer"
      locale={lang === "en" ? "en" : "uk"}
      browserFallback="customer"
      routeClass={routeClass}
      buildId={CLOSE_BUILD_ID}
      className="sv2-navbtn sv2-global-close"
      testId="sv2-global-app-close"
      onBeforeClose={armExplicitStoreClose}
      {...(onBrowserCloseFallback ? { onBrowserUiFallback: onBrowserCloseFallback } : {})}
      onAnalytics={emitCloseAnalytics}
    />
  );

  const control = !onRequestExitSheet ? (
    button
  ) : (
    <span
      className="sv2-global-close-exit-sheet-trigger"
      data-testid="sv2-global-close-exit-sheet-trigger"
      onClickCapture={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (openingRef.current) return;
        openingRef.current = true;
        emitEvent("exit_attempt", { route_class: routeClass });
        onRequestExitSheet();
        window.setTimeout(() => {
          openingRef.current = false;
        }, 400);
      }}
    >
      {button}
    </span>
  );

  return (
    <>
      <span className="sv2-close-control-spacer" aria-hidden="true" data-testid="sv2-close-control-spacer" />
      {mounted
        ? createPortal(
            <div
              className="sv2-close-control"
              data-testid="sv2-close-control"
              data-global-close-control="true"
              data-route-class={routeClass}
            >
              {control}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export function LangSwitch({
  lang,
  onChange,
}: {
  lang: StoreV2Lang;
  onChange: (lang: StoreV2Lang) => void;
}) {
  return (
    <div className="sv2-lang-switch" data-sv2-global-lang="true" role="group" aria-label="Language">
      <button
        type="button"
        className={`sv2-lang-btn${lang === "ua" ? " is-active" : ""}`}
        onClick={() => onChange("ua")}
      >
        UA
      </button>
      <button
        type="button"
        className={`sv2-lang-btn${lang === "en" ? " is-active" : ""}`}
        onClick={() => onChange("en")}
      >
        EN
      </button>
    </div>
  );
}

/** Global Store V2 header — Close, Back (nested), title, compact utilities. No hamburger. */
export function Chrome({
  title,
  cartCount,
  canGoBack,
  lang,
  theme = "system",
  routeClass = "store",
  visualSearchSlot,
  onBack,
  onHome,
  onCart,
  onProfile,
  onCabinet,
  onLangChange,
  onThemeCycle,
  onBrowserCloseFallback,
  onRequestExitSheet,
}: ChromeProps) {
  void onHome;
  void onCart;
  void cartCount;
  void onProfile;
  void onCabinet;
  const themeGlyph = theme === "dark" ? "☾" : theme === "light" ? "☀" : "◐";
  return (
    <>
    <header className="sv2-header" data-sv2-global-header="true">
      <div className="sv2-header-leading">
        <StoreV2GlobalClose
          lang={lang}
          routeClass={routeClass}
          {...(onBrowserCloseFallback ? { onBrowserCloseFallback } : {})}
          {...(onRequestExitSheet ? { onRequestExitSheet } : {})}
        />
        <span className="sv2-nav-back-slot" data-has-back={canGoBack ? "1" : "0"}>
          {canGoBack ? (
            <button type="button" className="sv2-navbtn sv2-navbtn-back" onClick={onBack} aria-label={t(lang, "back")}>
              <span className="sv2-nav-label-full">{t(lang, "back")}</span>
              <span className="sv2-nav-label-short" aria-hidden="true">
                ←
              </span>
            </button>
          ) : (
            <span className="sv2-nav-back-spacer" aria-hidden="true" />
          )}
        </span>
      </div>
      <span className="sv2-header-title">{title}</span>
      <div className="sv2-header-actions">
        {visualSearchSlot ? (
          <span className="sv2-header-vs" data-testid="sv2-global-visual-search">
            {visualSearchSlot}
          </span>
        ) : null}
        <CustomerFeedbackLauncher lang={lang} />
        {onLangChange ? <LangSwitch lang={lang} onChange={onLangChange} /> : null}
        {onThemeCycle ? (
          <button
            type="button"
            className="sv2-navbtn sv2-theme-toggle"
            data-sv2-theme-toggle="global"
            onClick={onThemeCycle}
            aria-label={lang === "en" ? `Theme: ${theme}` : `Тема: ${theme}`}
            title={lang === "en" ? `Theme: ${theme}` : `Тема: ${theme}`}
          >
            {themeGlyph}
          </button>
        ) : null}
      </div>
    </header>
    <CustomerFeedbackHost lang={lang} />
    </>
  );
}

export function ProductThumb({
  product,
  className,
  alt,
  previewable,
  variant = "card",
  index,
  cardPosition,
  mediaVariant,
  fetchPriority,
}: {
  product: CatalogV2Product;
  className: string;
  alt?: string;
  /** FUNCTIONALITY-112 — optional modal preview (never navigates to a broken URL). */
  previewable?: boolean;
  variant?: "card" | "mini" | "pdp";
  index?: number;
  cardPosition?: number;
  mediaVariant?: "original" | "card" | "mini";
  fetchPriority?: "high" | "low" | "auto";
}) {
  const [failed, setFailed] = useState(false);
  const [preview, setPreview] = useState(false);
  const resolvedVariant =
    mediaVariant === "original" ? "pdp" : mediaVariant === "mini" ? "mini" : mediaVariant === "card" ? "card" : variant;
  const src =
    resolvedVariant === "pdp"
      ? pdpImageUrl(product.imageUrl)
      : resolvedVariant === "mini"
        ? miniImageUrl(product.imageUrl)
        : cardImageUrl(product.imageUrl);
  if (!src || failed) {
    return <div className={`${className.replace("-img", "-noimg")}`}>🍜</div>;
  }
  const hints =
    cardPosition != null
      ? gridImageLoadingHints(cardPosition)
      : index != null
        ? gridImageLoadingHints(index)
        : resolvedVariant === "pdp"
          ? { loading: "eager" as const, fetchPriority: "high" as const }
          : { loading: "lazy" as const };
  const dims =
    resolvedVariant === "mini"
      ? { width: 56, height: 56 }
      : resolvedVariant === "pdp"
        ? { width: 640, height: 640 }
        : { width: 200, height: 200 };
  const img = (
    <img
      className={className}
      src={src}
      alt={alt ?? product.title}
      loading={hints.loading}
      decoding="async"
      width={dims.width}
      height={dims.height}
      {...(fetchPriority
        ? { fetchPriority }
        : hints.fetchPriority
          ? { fetchPriority: hints.fetchPriority }
          : {})}
      onError={() => setFailed(true)}
    />
  );
  if (!previewable) return img;
  const zoom = pdpImageUrl(product.imageUrl) ?? src;
  return (
    <>
      <button
        type="button"
        className="sv2-img-hit"
        onClick={() => setPreview(true)}
        aria-label="Переглянути фото"
      >
        {img}
      </button>
      {preview ? (
        <div
          className="sv2-img-modal"
          role="dialog"
          aria-modal="true"
          data-testid="sv2-product-image-modal"
          onClick={() => setPreview(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setPreview(false);
          }}
        >
          <button
            type="button"
            className="sv2-img-modal-close"
            data-testid="sv2-product-image-modal-close"
            aria-label="Закрити"
            onClick={(e) => {
              e.stopPropagation();
              setPreview(false);
            }}
          >
            ✕
          </button>
          <img src={zoom} alt={alt ?? product.title} onClick={(e) => e.stopPropagation()} />
        </div>
      ) : null}
    </>
  );
}

/**
 * STAGING-LAUNCH-READINESS-002-R2-R2 — order-line snapshot thumbnail (cabinet list/detail).
 * Always a plain image_url string (no CatalogV2Product) since the source is the immutable
 * order_items snapshot, not live catalog data. Placeholder on missing/broken image.
 */
export function OrderItemThumb({
  imageUrl,
  alt,
  className = "sv2-order-item-img",
}: {
  imageUrl: string | null;
  alt: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!imageUrl || failed) {
    return (
      <div
        className={`${className}-noimg`}
        aria-hidden="true"
        style={{ width: 48, height: 48, flex: "0 0 48px" }}
      >
        🍜
      </div>
    );
  }
  return (
    <img
      className={className}
      src={imageUrl}
      alt={alt}
      width={48}
      height={48}
      loading="lazy"
      decoding="async"
      style={{ width: 48, height: 48, objectFit: "cover", flex: "0 0 48px" }}
      onError={() => setFailed(true)}
    />
  );
}

export function metaLine(product: CatalogV2Product): string {
  return [product.brand, product.weight].filter(Boolean).join(" · ");
}

export interface ProductCardProps {
  lang: StoreV2Lang;
  product: CatalogV2Product;
  displayTitle: string;
  onOpen: (id: string) => void;
  onAdd: (product: CatalogV2Product) => void;
  /** MERCH-129A-R1 — open cart only (never add / qty change). */
  onOpenCart: () => void;
  /** Total qty across cart for badge (existing cart state). */
  cartItemCount?: number;
  cardPosition?: number;
  sourceSurface?: string;
  sourceRoute?: string;
  categoryId?: string | null;
  rankingVersion?: string;
  /** FUNCTIONALITY-113 — qty in cart for stepper. */
  qtyInCart?: number;
  onInc?: (productId: string) => void;
  onDec?: (productId: string) => void;
  onAvailabilityRequest?: (product: CatalogV2Product) => void;
  onPrefetch?: (product: CatalogV2Product) => void;
}

export function ProductCard({
  lang,
  product,
  displayTitle,
  onOpen,
  onAdd,
  onOpenCart,
  cartItemCount = 0,
  cardPosition,
  sourceSurface = "catalog",
  sourceRoute,
  categoryId = null,
  rankingVersion = "catalog_list_v1",
  qtyInCart = 0,
  onInc,
  onDec,
  onAvailabilityRequest,
  onPrefetch,
}: ProductCardProps) {
  const lowStock = product.stockStatus === "low_stock";
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const availability = resolveProductAvailability(product);
  const price = resolveCustomerVisiblePrice(product);
  const showCanaryBadge = isOwnerCanaryPriceViewer() && Boolean(product.canaryBadge);
  const identityCompact = resolveIdentityRows(lang === "en" ? "en" : "ua", {
    merchantSku: product.merchantSku,
    merchantArticle: product.merchantArticle,
    barcodeGtin: product.barcodeGtin,
    barcodeVerification: product.barcodeVerification,
    manufacturerPartNumber: null,
  });
  const primary = identityCompact.find((row) => row.kind === "store_sku" || row.kind === "barcode");
  const code = primary
    ? {
        value: primary.value,
        kind: (primary.kind === "barcode" ? "barcode" : "merchant_sku") as "barcode" | "merchant_sku",
      }
    : null;

  const addAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!added) return;
    const tmr = window.setTimeout(() => setAdded(false), 1200);
    return () => window.clearTimeout(tmr);
  }, [added]);

  useEffect(() => {
    if (qtyInCart <= 0) addAtRef.current = null;
  }, [qtyInCart]);

  const handleAdd = () => {
    if (!product.isSellable || busy) return;
    setBusy(true);
    addAtRef.current = Date.now();
    onAdd(product);
    setAdded(true);
    try {
      emitEvent("product_added", {
        product_id: product.id,
        source_surface: sourceSurface,
        source_route: sourceRoute ?? sourceSurface,
        locale: lang,
      });
    } catch {
      /* analytics soft-fail */
    }
    window.setTimeout(() => setBusy(false), 400);
  };

  /** product_card_cart_navigation / go-to-cart — navigate only; never mutates cart. */
  const handleOpenCart = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const addedAt = addAtRef.current;
    const latencyMs = addedAt != null ? Math.max(0, Date.now() - addedAt) : null;
    try {
      emitEvent("cart_opened_from_product_card", {
        product_id: product.id,
        merchant_sku: product.merchantSku ?? code?.value ?? null,
        category_id: categoryId,
        brand: product.brand ?? null,
        source_surface: sourceSurface,
        source_route: sourceRoute ?? sourceSurface,
        cart_item_count: cartItemCount,
        card_position: cardPosition ?? null,
        ranking_version: rankingVersion,
        locale: lang,
        timestamp: new Date().toISOString(),
        via: qtyInCart > 0 ? "go_to_cart_cta" : "cart_icon",
      });
      if (latencyMs != null) {
        emitEvent("add_to_cart_to_cart_latency", {
          product_id: product.id,
          latency_ms: latencyMs,
          source_surface: sourceSurface,
          locale: lang,
        });
      }
    } catch {
      /* analytics must never block cart navigation */
    }
    onOpenCart();
  };

  const cartNavLabel = lang === "en" ? "Go to cart" : "Перейти до кошика";
  const cartNavTitle = lang === "en" ? "Open cart" : "Відкрити кошик";

  return (
    <div
      className="sv2-card"
      data-testid="sv2-product-card"
      data-store-sku={code?.value || undefined}
      aria-description={code?.value ? `SKU ${code.value}` : undefined}
    >
      <button
        type="button"
        className="sv2-card-imgbtn"
        onPointerDown={() => onPrefetch?.(product)}
        onClick={() => onOpen(product.id)}
      >
        <ProductThumb
          product={product}
          className="sv2-card-img"
          alt={displayTitle}
          {...(cardPosition != null ? { cardPosition } : {})}
        />
        {lowStock ? <span className="sv2-badge-stock">{t(lang, "low_stock")}</span> : null}
        {showCanaryBadge ? (
          <span className="sv2-price-badge sv2-canary-badge">{product.canaryBadge}</span>
        ) : null}
      </button>
      <div className="sv2-card-body">
        {product.brand ? <div className="sv2-card-brand">{product.brand}</div> : null}
        <button
          type="button"
          className="sv2-card-title"
          onPointerDown={() => onPrefetch?.(product)}
          onClick={() => onOpen(product.id)}
        >
          {displayTitle}
        </button>
        {product.weight ? <div className="sv2-card-meta">{product.weight}</div> : null}
        <div className="sv2-card-price-row">
          {product.isPriced ? (
            <div className="sv2-card-price" data-testid="sv2-card-price">
              {price.saleActive ? (
                <>
                  <span className="sv2-price-sale">{formatUah(price.primary)}</span>{" "}
                  <span className="sv2-price-was">{formatUah(price.regular ?? 0)}</span>
                  {price.badge ? <span className="sv2-price-badge">{price.badge}</span> : null}
                </>
              ) : (
                formatUah(price.primary)
              )}
            </div>
          ) : (
            <div className="sv2-card-price sv2-unpriced" data-testid="sv2-card-price">
              {t(lang, "price_tbd")}
            </div>
          )}
          <button
            type="button"
            className="sv2-card-cart-nav"
            data-testid="sv2-card-cart-nav"
            data-capability="product_card_cart_navigation"
            aria-label={cartNavLabel}
            title={cartNavTitle}
            onClick={handleOpenCart}
          >
            <StoreV2Icon name="cart" size={20} />
            {cartItemCount > 0 ? (
              <span className="sv2-card-cart-badge" data-testid="sv2-card-cart-badge" aria-hidden="true">
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            ) : null}
          </button>
        </div>
        {code ? (
          <div className="sv2-card-sku">
            {customerCodeLabel(lang, code.kind)}: {code.value}
          </div>
        ) : null}
        <div className={`sv2-availability sv2-availability-${availability.state}`}>
          {availability.labelUa}
        </div>
        <div className={`sv2-card-actions`} data-testid="sv2-card-actions" data-has-qty={qtyInCart > 0 ? "1" : "0"}>
          {qtyInCart > 0 && onInc && onDec ? (
            <>
              <div className="sv2-qty-stepper" data-testid="sv2-card-qty-stepper">
                <button
                  type="button"
                  className="sv2-qty-btn"
                  disabled={busy}
                  aria-label={lang === "en" ? "Decrease quantity" : "Зменшити кількість"}
                  onClick={() => onDec(product.id)}
                >
                  −
                </button>
                <span className="sv2-qty-val" aria-live="polite">
                  {qtyInCart}
                </span>
                <button
                  type="button"
                  className="sv2-qty-btn"
                  disabled={!product.isSellable || busy}
                  aria-label={lang === "en" ? "Increase quantity" : "Збільшити кількість"}
                  onClick={() => onInc(product.id)}
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className="sv2-add sv2-add-to-cart-cta"
                data-testid="sv2-card-go-to-cart"
                data-capability="product_card_go_to_cart"
                data-cart-total-qty={cartItemCount}
                data-sku-qty={qtyInCart}
                onClick={handleOpenCart}
              >
                {/* R7.2.2F4C — same total as badge; stepper alone shows SKU qty */}
                {t(lang, "cart_cta_go", { qty: String(cartItemCount) })}
              </button>
            </>
          ) : availability.canAdd ? (
            <button
              type="button"
              className={`sv2-add${added ? " sv2-add-pulse" : ""}`}
              data-testid="sv2-card-add-to-cart"
              disabled={busy}
              onClick={handleAdd}
            >
              {added ? t(lang, "added_btn") : t(lang, "add_to_cart")}
            </button>
          ) : (
            <button
              type="button"
              className="sv2-add"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onAvailabilityRequest?.(product);
              }}
            >
              {t(lang, "availability_request")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export type ContactToastKind = "ok" | "err" | null;

export function ManagerContactButton({
  lang,
  onContact,
  toast,
  buttonRef,
}: {
  lang: StoreV2Lang;
  onContact: () => void;
  /** Short toast after click only (auto-clears in parent). */
  toast?: { kind: ContactToastKind; text: string } | null;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <div className="sv2-contactbar">
      <button ref={buttonRef} type="button" className="sv2-contact-btn" onClick={onContact}>
        💬 {t(lang, "contact_manager")}
      </button>
      {toast?.kind === "ok" ? <div className="sv2-contact-toast sv2-contact-toast-ok">{toast.text}</div> : null}
      {toast?.kind === "err" ? <div className="sv2-contact-toast sv2-contact-toast-err">{toast.text}</div> : null}
    </div>
  );
}

export function ContactPhoneModal({
  phoneDisplay,
  phoneTel,
  email,
  telegramUrl,
  onClose,
  onTrack,
}: {
  phoneDisplay: string;
  phoneTel: string;
  email: string;
  telegramUrl?: string | null;
  onClose: () => void;
  onTrack?: (event: "call" | "viber" | "telegram" | "email" | "fallback") => void;
}) {
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const viberNumber = `%2B${phoneTel.replace(/^\+/, "")}`;

  const copyPhone = async () => {
    if (await copyText(phoneTel)) {
      setCopied(true);
      setMessage(null);
      window.setTimeout(() => setCopied(false), 1800);
    } else {
      setMessage(`Скопіюйте вручну: ${phoneDisplay}`);
    }
  };

  const openTelegram = () => {
    onTrack?.("telegram");
    if (!telegramUrl) {
      setMessage("На компʼютері відкрийте Viber/Telegram вручну або скопіюйте телефон.");
      onTrack?.("fallback");
      return;
    }
    try {
      const tg = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void; openLink?: (url: string) => void } } }).Telegram?.WebApp;
      if (telegramUrl.includes("t.me/") && tg?.openTelegramLink) tg.openTelegramLink(telegramUrl);
      else if (tg?.openLink) tg.openLink(telegramUrl);
      else window.open(telegramUrl, "_blank", "noopener");
    } catch {
      setMessage("На компʼютері відкрийте Viber/Telegram вручну або скопіюйте телефон.");
      onTrack?.("fallback");
    }
  };
  const schemeClick = (kind: "call" | "viber") => {
    onTrack?.(kind);
    if (!/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "")) {
      setMessage("На компʼютері відкрийте Viber/Telegram вручну або скопіюйте телефон.");
      onTrack?.("fallback");
    }
  };

  return (
    <div className="sv2-modal-overlay" role="dialog" aria-modal="true" aria-label="Контакт менеджера">
      <div className="sv2-modal-card">
        <div className="sv2-section-title">Телефон менеджера</div>
        <p className="sv2-modal-phone">{phoneDisplay}</p>
        <a className="sv2-cta sv2-contact-modal-action" href={`tel:${phoneTel}`} onClick={() => schemeClick("call")}>
          📞 Подзвонити
        </a>
        <a
          className="sv2-cta sv2-secondary sv2-contact-modal-action"
          href={`viber://chat?number=${viberNumber}`}
          onClick={() => schemeClick("viber")}
        >
          💬 Viber
        </a>
        <button type="button" className="sv2-cta sv2-secondary" onClick={openTelegram}>
          ✈️ Telegram
        </button>
        <a
          className="sv2-cta sv2-secondary sv2-contact-modal-action"
          href={`mailto:${email}`}
          onClick={() => onTrack?.("email")}
        >
          ✉️ Email
        </a>
        <button type="button" className="sv2-cta sv2-secondary" onClick={() => void copyPhone()}>
          {copied ? "Скопійовано ✓" : "📋 Скопіювати телефон"}
        </button>
        {message ? <div className="sv2-alert sv2-alert-err">{message}</div> : null}
        <button type="button" className="sv2-cta sv2-secondary" onClick={onClose}>
          Закрити
        </button>
      </div>
    </div>
  );
}

export function BottomQuickNav({
  lang,
  onBack,
  onHome,
  onCart,
  hideCart,
  hideHome,
  homePrimary,
  backLabel,
}: {
  lang: StoreV2Lang;
  onBack?: (() => void) | undefined;
  onHome?: (() => void) | undefined;
  onCart?: (() => void) | undefined;
  /** FUNCTIONALITY-112 — hide “До кошика” when already in cart. */
  hideCart?: boolean;
  hideHome?: boolean;
  /** FUNCTIONALITY-113 — larger home CTA in cart. */
  homePrimary?: boolean;
  backLabel?: string | undefined;
}) {
  return (
    <div className={`sv2-bottom-quick${homePrimary && hideCart ? " is-home-only" : ""}`}>
      {onBack ? (
        <button type="button" className="sv2-cta sv2-secondary" onClick={onBack}>
          {backLabel ?? (lang === "en" ? "← Back" : "← Назад")}
        </button>
      ) : null}
      {!hideHome && onHome ? (
        <button
          type="button"
          className={`sv2-cta sv2-soft${homePrimary ? " sv2-home-primary" : ""}`}
          onClick={onHome}
        >
          {lang === "en" ? "Home" : "На головну"}
        </button>
      ) : null}
      {hideCart || !onCart ? null : (
        <button type="button" className="sv2-cta" onClick={onCart}>
          {lang === "en" ? "To cart" : "До кошика"}
        </button>
      )}
    </div>
  );
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return <div className="sv2-toast" role="status">{message}</div>;
}

export function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="sv2-center">
      <div className="sv2-spinner" />
      {label}
    </div>
  );
}

/** COMMERCE-LAUNCH-163A-R2C — branded store boot (no system diagnostics). */
export function BrandedStoreLoading({ lang }: { lang: StoreV2Lang }) {
  return (
    <div className="sv2-center" data-testid="sv2-branded-loading">
      <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>
        🐉
      </div>
      <div style={{ fontWeight: 700, marginTop: 12, fontSize: 18 }}>
        {t(lang, "loading_brand_title")}
      </div>
      <div className="sv2-muted" style={{ marginTop: 6 }}>
        {t(lang, "loading")}
      </div>
      <div className="sv2-spinner" style={{ marginTop: 18 }} />
    </div>
  );
}

/**
 * STAGING-LAUNCH-READINESS-002-R2-R2 — degraded state shown after a route's
 * loading gate times out (~8s), replacing an infinite spinner with a Retry +
 * Home affordance. `onRetry` must re-attempt the SAME in-app load (never
 * window.location.reload — that would re-boot the whole catalog/session).
 * COMMERCE-LAUNCH-163A-R2C — store variant: no HTTP codes / build markers.
 */
export function RouteDegradedState({
  lang,
  onRetry,
  onHome,
  onContactManager,
  variant = "route",
}: {
  lang: StoreV2Lang;
  onRetry: () => void;
  onHome: () => void;
  onContactManager?: () => void;
  variant?: "route" | "store";
}) {
  const en = lang === "en";
  const isStore = variant === "store";
  return (
    <div className="sv2-empty" data-testid="sv2-route-degraded">
      <div className="sv2-empty-emoji">{isStore ? "🐉" : "⏳"}</div>
      <div style={{ fontWeight: 700, marginTop: 8 }}>
        {isStore
          ? t(lang, "catalog_load_fail_title")
          : en
            ? "This is taking longer than usual"
            : "Це триває довше, ніж зазвичай"}
      </div>
      {!isStore ? (
        <div className="sv2-muted" style={{ marginTop: 6 }}>
          {en ? "Check your connection and try again." : "Перевірте з'єднання та спробуйте ще раз."}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", justifyContent: "center" }}>
        <button type="button" className="sv2-cta" data-testid="sv2-route-degraded-retry" onClick={onRetry}>
          {t(lang, "retry")}
        </button>
        {isStore && onContactManager ? (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-route-degraded-manager"
            onClick={onContactManager}
          >
            {t(lang, "contact_manager")}
          </button>
        ) : (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-route-degraded-home"
            onClick={onHome}
          >
            {en ? "Home" : "На головну"}
          </button>
        )}
      </div>
    </div>
  );
}

export function EmptyState({
  emoji,
  title,
  hint,
}: {
  emoji: string;
  title: string;
  hint?: string | undefined;
}) {
  return (
    <div className="sv2-empty">
      <div className="sv2-empty-emoji">{emoji}</div>
      <div style={{ fontWeight: 700, marginTop: 8 }}>{title}</div>
      {hint ? <div className="sv2-muted" style={{ marginTop: 6 }}>{hint}</div> : null}
    </div>
  );
}

/**
 * COMMERCE-LAUNCH-168D1 — page/catalog SearchBar uses local draft (same IME-safe pattern as IsolatedSearchBar).
 * Keystrokes must NOT lift state into CatalogListV2/ProductV2 (re-renders kill Android Telegram/SwiftKey).
 * `value`/`onChange` kept for API compat; live typing stays local; parent notified on submit only.
 */
export function SearchBar({
  lang,
  value,
  onChange,
  onSubmit,
  visualSlot,
}: {
  lang: StoreV2Lang;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  visualSlot?: React.ReactNode;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const submit = () => {
    const q = draft.trim();
    onChange(q);
    // Prefer IsolatedSearchBar at call sites; this path keeps draft authoritative for Enter/button.
    onSubmit();
  };

  const search = (
    <div className="sv2-search" data-isolated-search="1">
      <input
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={
          lang === "en"
            ? "Search: tom yum, ramen, sauce, brand or SKU…"
            : "Пошук: том ям, рамен, соус, бренд або SKU…"
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
      />
      <button type="button" onClick={submit}>
        {lang === "en" ? "Search" : "Пошук"}
      </button>
    </div>
  );
  if (!visualSlot) return search;
  return (
    <div className="sv2-search-row" data-testid="sv2-search-row">
      {search}
      {visualSlot}
    </div>
  );
}
